import { NavLink } from 'react-router-dom';
import { LayoutGrid, BarChart2, Users, ShieldCheck, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

const baseNavItems = [
  { to: '/kanban',     label: 'Funil',      Icon: LayoutGrid, adminOnly: false },
  { to: '/analytics',  label: 'Analytics',  Icon: BarChart2,  adminOnly: false },
  { to: '/candidates', label: 'Candidatos', Icon: Users,      adminOnly: false },
  { to: '/users',      label: 'Usuários',   Icon: ShieldCheck, adminOnly: true  },
];

interface Props {
  mobileOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onClose }: Props) {
  const { user } = useAuth();
  const navItems = baseNavItems.filter((item) => !item.adminOnly || user?.role === 'admin');

  return (
    <>
      {/* ── Mobile backdrop ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-[#1C1915]/30 backdrop-blur-[2px] md:hidden"
          onClick={onClose}
        />
      )}

      {/* ── Sidebar panel ── */}
      <aside
        className={cn(
          'flex h-full w-52 shrink-0 flex-col border-r border-border bg-bg-surface',
          'md:relative md:translate-x-0 md:z-auto',
          'fixed inset-y-0 left-0 z-40 transition-transform duration-200 ease-out md:transition-none',
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
      >
        {/* ── Brand ── */}
        <div className="flex h-14 items-center justify-between px-5 border-b border-border shrink-0">
          <img
            src="/logo-nobel-full.png"
            alt="Nobel Capital"
            style={{ height: '36px', width: 'auto', display: 'block', flexShrink: 0 }}
          />

          {/* close — mobile only */}
          <button
            onClick={onClose}
            className="md:hidden ml-2 shrink-0 flex items-center justify-center h-7 w-7 rounded-sm text-text-muted hover:text-text-primary hover:bg-bg-elevated transition-colors"
            aria-label="Fechar menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Nav ── */}
        <nav className="flex flex-1 flex-col gap-px p-2 pt-3">
          {navItems.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-2.5 rounded-sm px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-bg-elevated text-text-primary font-medium'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated/60',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 transition-colors',
                      isActive ? 'text-accent' : 'text-text-muted group-hover:text-text-secondary',
                    )}
                  />
                  {label}
                  {isActive && (
                    <span className="ml-auto h-1 w-1 rounded-full bg-accent shrink-0" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* ── Footer ── */}
        <div className="border-t border-border px-5 py-3 shrink-0">
          <p className="text-2xs text-text-muted tracking-wide">v1.0</p>
        </div>
      </aside>
    </>
  );
}
